import { lazy, type ComponentType } from "react";

/**
 * Route-level lazy loading that survives a new release.
 *
 * When the app is updated, the previously loaded page index points at chunk
 * files that no longer exist, so the dynamic import fails with
 * "Failed to fetch dynamically imported module" and the screen goes blank.
 * We retry once after a short pause (covers a flaky network), then reload the
 * page once to pick up the new file names. A session flag stops reload loops.
 */
const RELOAD_FLAG = "nursly:chunk-reloaded";

export function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await importer();
      sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (err) {
      try {
        await new Promise((r) => setTimeout(r, 400));
        const mod = await importer();
        sessionStorage.removeItem(RELOAD_FLAG);
        return mod;
      } catch (retryErr) {
        const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === "1";
        if (!alreadyReloaded) {
          sessionStorage.setItem(RELOAD_FLAG, "1");
          window.location.reload();
          // Keep the promise pending while the page reloads.
          return new Promise<{ default: T }>(() => {});
        }
        throw retryErr;
      }
    }
  });
}
